// Behaviour half of the Social::Profile::Autotest `download_wall_treatment`
// slot: intercepts a work-card download click and opens the shared login modal
// (js/login_modal) instead of navigating to /signup.
//
// doc_id must travel with the registration POST. Post-signup routing reads
// session[:doc_id]; the signup page writes it at render time from params[:a_id]
// and a profile page cannot, because at render time it does not know which paper
// will be clicked. Api::V0::RegistrationsController mirrors the POSTed doc_id
// into the session before computing the redirect. Drop it and the visitor is
// promised a paper and lands on the generic reg path.
//
// NOT an entry in ARMS: harness.resolveMode() returns null without a currentUser,
// and this surface buckets server-side. It is a sibling of setUpProfileAutotest.
import type { LoginModalEventDetail } from 'js/login_modal/LoginModalManager';
import routes from 'js/core_pre_webpack/routes';
import trackClick from 'js/utils/trackClick';

import { lazyMountLoginModal } from './lazyMountLoginModal';
import { downloadLinkFor, titleFor } from './surface';

// Presence is the bucket; absent under the control default, and then this module
// binds nothing at all.
const CONFIG_ELEMENT = '.js-profile-signup-wall';

// Source 17, matching control's signup_url(a_id:, s: 'profile') redirect. Do not
// change to 'download' (source 10): that re-attaches the category-'download' side
// effects — from_user_id, RecordWorkViewJob, KII, the SUTD funnel — that the q3_v2
// attribution fix removed from profile signups.
const SIGNUP_SOURCE = 'profile';

// The paper is never in here; it is read from the clicked card's DOM at click
// time. Everything else the modal accepts is a constant below — no arm varies it.
type WallConfig = {
  body?: string;
};

// The only header variant that renders workTitle, and the only one that renders
// buttonsPage.header is 'default' — so there is no `heading` config field.
// YourPaperCard falls back to "this paper", so passing it unconditionally is safe
// even when cardFor finds no title (rail and hero buttons share the click-track).
const HEADER_VARIANT = 'yourPaperCard';

// From the href. The work cards' data-download attribute is a Ruby hash with
// unquoted keys and JSON.parse throws on it; the autotest's own CTAs do not emit
// it at all, because nothing in the repo reads it.
function attachmentIdFrom(link: HTMLAnchorElement): string | null {
  const match = /\/attachments\/(\d+)\//.exec(link.getAttribute('href') ?? '');

  return match == null ? null : match[1];
}

function readConfig(element: HTMLElement): WallConfig {
  try {
    return JSON.parse(element.dataset.wallConfig ?? '{}') as WallConfig;
  } catch {
    // A malformed config is a reason to open the modal with its own defaults,
    // never a reason to break the download click.
    return {};
  }
}

// Counted, not a boolean: the cursor is a single global, so two overlapping clicks
// would otherwise have the first one to finish clear the second's busy state.
let pendingOpens = 0;

function setPending(link: HTMLAnchorElement, pending: boolean): void {
  link.setAttribute('aria-busy', String(pending));
  pendingOpens = Math.max(0, pendingOpens + (pending ? 1 : -1));
  document.documentElement.style.cursor = pendingOpens > 0 ? 'progress' : '';
}

function isPlainClick(event: MouseEvent): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

function detailFor(
  config: WallConfig,
  attachmentId: string,
  title: string | null,
): LoginModalEventDetail {
  // No cta_label field: the modal has four provider buttons whose labels its own
  // ConfigurableText keeps in step with the legal disclaimer verb, so renaming one
  // would desynchronise the disclaimer.
  const buttonsPage: Record<string, string> = {};
  if (config.body != null) buttonsPage.subheader = config.body;

  return {
    // No postLoginRedirectUrl on purpose: navigateOnSuccess prefers the server's
    // redirect, which is already control's destination once doc_id is in session.
    registrationParams: {
      user_source: SIGNUP_SOURCE,
      doc_id: attachmentId,
    },
    configuredText:
      Object.keys(buttonsPage).length > 0 ? { buttonsPage } : undefined,
    clickTrackingContext: {
      entityId: attachmentId,
      extra: { wall: 'profile-signup-wall' },
    },
    headerVariant: HEADER_VARIANT,
    workTitle: title ?? undefined,
  };
}

export const setUpProfileSignupWall = (): void => {
  const configElement = document.querySelector<HTMLElement>(CONFIG_ELEMENT);
  // Unfilled slot, logged-in viewer, or a crawler on control: bind nothing.
  if (configElement == null) return;

  const config = readConfig(configElement);

  // Bubble phase and NO stopPropagation: the page's own data-click-track handler
  // must still record profile-work-strip-download at control's rate, or the
  // click-proximity metric this wave is read on stops being comparable.
  document.addEventListener('click', (event: MouseEvent) => {
    if (!isPlainClick(event)) return;

    const link = downloadLinkFor(event.target);
    if (link == null) return;

    const attachmentId = attachmentIdFrom(link);
    // Let the browser navigate: the server redirect is the fallback for every
    // path this module cannot finish, as it is for a visitor with no JS.
    if (attachmentId == null) return;

    event.preventDefault();

    const detail = detailFor(config, attachmentId, titleFor(event.target));

    trackClick({
      source: 'profile-signup-wall-open',
      entityId: attachmentId,
    });

    // preventDefault() has already cancelled the navigation, so between here and
    // the modal opening the button would otherwise look dead. No markup or CSS is
    // added for this: the platform's own busy cursor plus aria-busy is enough, and
    // anything richer would be arm-only DOM on the largest organic landing page.
    setPending(link, true);

    lazyMountLoginModal(detail)
      .finally(() => setPending(link, false))
      .catch(() => {
        // Chunk failed to load. This is the ONLY path for every visitor whose
        // fetch fails or who misses the deadline, so it goes through the route
        // helper — control reaches the absolute signup_url(a_id:, s: 'profile'),
        // and a hand-built relative path is only approximately that.
        window.location.href = routes.signup_url({
          a_id: attachmentId,
          s: SIGNUP_SOURCE,
        });
      });
  });
};

export default setUpProfileSignupWall;
